import React from 'react';
import { ArrowRight, ShieldCheck, FileCheck2, Radar, Network, Landmark, Banknote, HeartPulse, GraduationCap, Truck, Zap, Download, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Eyebrow, SectionTitle, GridBg, GlowOrb } from '../components/SectionHeader';
import { services, industries, heroStats, businessInfo } from '../mock';
import { capabilityDownloadUrl, capabilityMeta } from '../api';

const iconMap = { ShieldCheck, FileCheck2, Radar, Network, Landmark, Banknote, HeartPulse, GraduationCap, Truck, Zap };

const Home = ({ onContact }) => {
  const [capAvailable, setCapAvailable] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    capabilityMeta()
      .then(m => { if (!cancelled) setCapAvailable(!!m.available); })
      .catch(err => {
        console.warn('Capability meta fetch failed:', err?.message || err);
        if (!cancelled) setCapAvailable(false);
      });
    return () => { cancelled = true; };
  }, []);
  return (
    <div className="bg-[#0a0e1a]">
      {/* HERO */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <GridBg />
        <GlowOrb className="w-[600px] h-[600px] -top-40 -left-40" />
        <GlowOrb className="w-[500px] h-[500px] top-20 right-0" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cyan-400/40 bg-cyan-400/5 text-cyan-300 text-xs font-medium">
              <ShieldCheck className="w-3.5 h-3.5" /> Cybersecurity & GRC Solutions
            </span>
            <h1 className="mt-6 text-5xl md:text-7xl font-bold text-white leading-[1.05] tracking-tight">
              Security Concern?<br />
              <span className="text-cyan-400">We Can Help.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
              IronVision provides cybersecurity and GRC services to U.S. Department of Defense agencies, the Intelligence Community, and other federal and regulated organizations. We deliver mission-focused solutions tailored to each client&apos;s requirements.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Button onClick={onContact} className="bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold h-12 px-7 rounded-md shadow-[0_0_28px_rgba(34,211,238,0.4)]">
                Contact Us <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <Button asChild variant="outline" className="border-slate-600 bg-slate-800/40 hover:bg-slate-800 hover:border-cyan-400/40 text-white h-12 px-7 rounded-md">
                <a href="/products">Explore GRC Platform</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* BUSINESS INFO */}
      <section className="py-16 border-y border-cyan-400/10 bg-[#080c17]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h3 className="text-center text-xl font-semibold text-white mb-10">Business Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {businessInfo.map((b) => (
              <div key={b.label} className="rounded-xl border border-cyan-400/10 bg-gradient-to-b from-slate-900/40 to-transparent p-6 hover:border-cyan-400/30 transition">
                <p className="text-xs tracking-[0.25em] text-fuchsia-400 uppercase">{b.label}</p>
                <p className="mt-3 text-white font-mono text-base break-words">{b.value}</p>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-10">
            {capAvailable ? (
              <a href={capabilityDownloadUrl()} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-cyan-300 hover:text-cyan-200 text-sm font-medium px-5 py-2.5 rounded-md border border-cyan-400/30 hover:border-cyan-400/60 bg-cyan-400/5 transition">
                <Download className="w-4 h-4" /> Download our Capabilities Statement
              </a>
            ) : (
              <span className="inline-flex items-center gap-2 text-slate-500 text-sm">
                <Download className="w-4 h-4" /> Capabilities Statement coming soon
              </span>
            )}
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="py-28 relative overflow-hidden">
        <GlowOrb className="w-[450px] h-[450px] -bottom-20 -right-20" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <Eyebrow>Mission Statement</Eyebrow>
            <SectionTitle>Dedicated to the <span className="text-cyan-400">Warfighter</span></SectionTitle>
            <p className="mt-6 text-slate-300 leading-relaxed">
              IronVision is dedicated to the Warfighter and aims to protect and provide service to the U.S. Department of Defense entities and the Intelligence Community.
            </p>
            <p className="mt-4 text-slate-400 leading-relaxed">We partner with federal agencies and industry to assess risk, strengthen security posture, and support the resilience of critical infrastructure that the American people rely on.</p>
          </div>
          <div className="rounded-2xl border border-cyan-400/15 bg-gradient-to-br from-slate-900/60 to-slate-950/30 p-8">
            <h3 className="text-2xl font-bold text-white mb-6">Our Differentiators</h3>
            <ul className="space-y-4">
              {['Boutique-style, tailored solutions—not one-size-fits-all', 'Deep expertise in Department of Defense and Intelligence Community', 'Protect confidentiality, integrity, and availability of your systems', 'Handle all aspects of network security so you can focus on your business'].map(b => (
                <li key={b} className="flex gap-3 text-slate-200">
                  <CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-28 bg-[#080c17] border-y border-cyan-400/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <Eyebrow>Services</Eyebrow>
            <SectionTitle>Our Cybersecurity <span className="text-cyan-400">Services</span></SectionTitle>
            <p className="mt-5 text-slate-400 max-w-2xl mx-auto">Cybersecurity requirements vary by mission and environment. IronVision designs and delivers solutions aligned to each client&apos;s operational needs and compliance obligations.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map(s => {
              const Icon = iconMap[s.icon];
              return (
                <div key={s.title} className="group rounded-2xl border border-cyan-400/10 bg-slate-900/40 p-7 hover:border-cyan-400/40 hover:bg-slate-900/70 hover:-translate-y-1 transition duration-300">
                  <div className="w-12 h-12 rounded-lg bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center group-hover:bg-cyan-400/20 transition">
                    {Icon && <Icon className="w-6 h-6 text-cyan-400" />}
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-white">{s.title}</h3>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">{s.description}</p>
                </div>
              );
            })}
          </div>
          <div className="flex justify-center mt-14">
            <Button asChild variant="outline" className="border-cyan-400/40 bg-transparent hover:bg-cyan-400/10 text-cyan-300 rounded-md h-11 px-6">
              <a href="/products">Learn about our GRC Platform <ArrowRight className="w-4 h-4 ml-1" /></a>
            </Button>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-28 relative overflow-hidden">
        <GlowOrb className="w-[500px] h-[500px] top-10 -left-40" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <Eyebrow>Industries</Eyebrow>
            <SectionTitle>Industries We <span className="text-cyan-400">Serve</span></SectionTitle>
            <p className="mt-5 text-slate-400 max-w-2xl mx-auto">We provide specialized cybersecurity solutions across critical sectors of the economy.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map(ind => {
              const Icon = iconMap[ind.icon];
              return (
                <div key={ind.title} className="rounded-2xl border border-cyan-400/10 bg-slate-900/30 p-7 hover:border-cyan-400/30 transition">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-lg bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
                      {Icon && <Icon className="w-5 h-5 text-cyan-400" />}
                    </div>
                    <h3 className="text-xl font-semibold text-white">{ind.title}</h3>
                  </div>
                  <p className="mt-4 text-sm text-slate-400 leading-relaxed">{ind.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-20 border-y border-cyan-400/10 bg-[#070b15]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-8">
          {heroStats.map(s => (
            <div key={s.label} className="text-center">
              <p className="text-5xl md:text-6xl font-bold text-cyan-400">{s.value}</p>
              <p className="mt-2 text-sm text-slate-400 tracking-wide uppercase">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 relative overflow-hidden">
        <GlowOrb className="w-[600px] h-[600px] top-0 left-1/2 -translate-x-1/2" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-6xl font-bold text-white leading-tight">
            Reduce Risk,<br /><span className="text-cyan-400">Increase Efficiency</span>
          </h2>
          <p className="mt-6 text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Let us handle all aspects of your network security so that you can focus on the most critical asset—your business.
          </p>
          <Button onClick={onContact} className="mt-10 bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold h-12 px-8 rounded-md shadow-[0_0_28px_rgba(34,211,238,0.4)]">
            Get Started <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Home;
